// src/services/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ✅ توکن‌ها دیگر در localStorage ذخیره نمی‌شوند — کوکی httpOnly این کار را
// انجام می‌دهد و مرورگر خودکار آن را با هر درخواست می‌فرستد (credentials: 'include').
// فقط نیاز داریم توکن CSRF را از کوکی غیر-httpOnly بخوانیم و در هدر بفرستیم.
const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const createHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };
  const csrfToken = getCsrfToken();
  if (csrfToken) headers['X-CSRFToken'] = csrfToken;
  return headers;
};

const handleResponse = async (response) => {
  // 204 No Content (مثل DELETE) - بدون body
  if (response.status === 204) return { success: true };

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || 'API Error');
  }
  return data;
};

export const apiService = {
  // ✅ باید یک‌بار موقع بالا آمدن اپ صدا زده بشه تا کوکی csrftoken ست بشه —
  // بدون این، هیچ POST/PUT/DELETE ای کار نمی‌کنه (403 می‌گیره)
  primeCsrf: async () => {
    try {
      await fetch(`${API_BASE_URL}/csrf/`, { credentials: 'include' });
    } catch (e) {
      console.error('CSRF priming failed:', e);
    }
  },

  // ─── Auth ──────────────────────────────────────────────────────────
  login: async (credentials) => {
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(credentials),
    });
    const data = await handleResponse(response);
    // فقط اطلاعات نمایشی کاربر (غیرحساس) در localStorage نگه داشته می‌شود
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  },

  register: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/register/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(userData),
    });
    const data = await handleResponse(response);
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  },

  refreshToken: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  logout: async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout/`, {
      credentials: 'include',
        method: 'POST',
        headers: createHeaders(),
      });
    } catch (e) {
      // حتی اگه درخواست شبکه شکست خورد، اطلاعات محلی رو پاک می‌کنیم
    }
    localStorage.removeItem('user');
    localStorage.removeItem('cart');
    localStorage.removeItem('wishlist');
  },

  // ─── Profile ───────────────────────────────────────────────────────
  getProfile: async () => {
    const response = await fetch(`${API_BASE_URL}/profile/`, {
      credentials: 'include',
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  updateProfile: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/profile/update/`, {
      credentials: 'include',
      method: 'PATCH',
      headers: createHeaders(),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  // ─── Categories ────────────────────────────────────────────────────
  getCategories: async () => {
    const response = await fetch(`${API_BASE_URL}/categories/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  // ─── Products ──────────────────────────────────────────────────────
  getProducts: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.category) searchParams.append('category', params.category);
    if (params.search)   searchParams.append('search', params.search);
    if (params.discount) searchParams.append('discount', params.discount);
    if (params.light)    searchParams.append('light', params.light);

    const qs  = searchParams.toString();
    const url = qs ? `${API_BASE_URL}/products/?${qs}` : `${API_BASE_URL}/products/`;

    const response = await fetch(url, {
      credentials: 'include', headers: createHeaders() });
    return handleResponse(response);
  },

  getProduct: async (id) => {
    const response = await fetch(`${API_BASE_URL}/products/${id}/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  // ─── Gold Prices ───────────────────────────────────────────────────
  getGoldPrices: async () => {
    const response = await fetch(`${API_BASE_URL}/gold-prices/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },
  // نکته: endpoint محاسبه‌ی قیمت (calculate-price) در backend وجود ندارد،
  // بنابراین از این سرویس حذف شد. در صورت نیاز باید در backend ساخته شود.

  // ─── Cart ──────────────────────────────────────────────────────────
  getCart: async () => {
    const response = await fetch(`${API_BASE_URL}/cart/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  addToCart: async (productId, quantity = 1) => {
    const response = await fetch(`${API_BASE_URL}/cart/add/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ product_id: productId, quantity }),
    });
    return handleResponse(response);
  },

  removeFromCart: async (itemId) => {
    const response = await fetch(`${API_BASE_URL}/cart/remove/${itemId}/`, {
      credentials: 'include',
      method: 'DELETE',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  updateCartQuantity: async (itemId, quantity) => {
    const response = await fetch(`${API_BASE_URL}/cart/update/${itemId}/`, {
      credentials: 'include',
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify({ quantity }),
    });
    return handleResponse(response);
  },

  // ─── ✅ Wishlist (جدید) ─────────────────────────────────────────────
  /**
   * دریافت لیست علاقه‌مندی‌های کاربر لاگین‌شده
   * GET /api/wishlist/
   */
  getWishlist: async () => {
    const response = await fetch(`${API_BASE_URL}/wishlist/`, {
      credentials: 'include',
      method: 'GET',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * افزودن محصول به لیست علاقه‌مندی‌ها
   * POST /api/wishlist/   body: { product_id: <int> }
   */
  addToWishlist: async (productId) => {
    const response = await fetch(`${API_BASE_URL}/wishlist/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ product_id: productId }),
    });
    return handleResponse(response);
  },

  /**
   * حذف محصول از لیست علاقه‌مندی‌ها
   * DELETE /api/wishlist/<product_id>/
   */
  removeFromWishlist: async (productId) => {
    const response = await fetch(`${API_BASE_URL}/wishlist/${productId}/`, {
      credentials: 'include',
      method: 'DELETE',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  // ─── Wallet ────────────────────────────────────────────────────────
  // موجودی کیف پول از طریق getProfile در دسترس است (wallet_balance)؛
  // endpoint مستقل GET /wallet/ در backend وجود ندارد.
  addToWallet: async (amount) => {
    const response = await fetch(`${API_BASE_URL}/wallet/add/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ amount }),
    });
    return handleResponse(response);
  },

  // ─── Gold Bank ─────────────────────────────────────────────────────
  getGoldBank: async () => {
    const response = await fetch(`${API_BASE_URL}/gold-bank/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  buyGold: async (data) => {
    const response = await fetch(`${API_BASE_URL}/gold-bank/buy/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  sellGold: async (data) => {
    const response = await fetch(`${API_BASE_URL}/gold-bank/sell/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  getGoldTransactions: async () => {
    const response = await fetch(`${API_BASE_URL}/gold-bank/transactions/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },
  // نکته: endpoint برنامه‌های پس‌انداز (saving-plans) در backend وجود ندارد
  // (مدل SavingPlan هست ولی API آن ساخته نشده) — در صورت نیاز باید اضافه شود.

  // ─── Orders ────────────────────────────────────────────────────────
  getOrders: async () => {
    const response = await fetch(`${API_BASE_URL}/orders/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  createOrder: async (orderData) => {
    const response = await fetch(`${API_BASE_URL}/checkout/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(orderData),
    });
    return handleResponse(response);
  },

  // ─── Transactions ──────────────────────────────────────────────────
  getTransactions: async () => {
    const response = await fetch(`${API_BASE_URL}/reports/transactions/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },
  // ─── Consultation ───────────────────────────────────────────────────────────
  submitConsultation: async (formData) => {
    const response = await fetch(`${API_BASE_URL}/consultation/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(formData),
    });
    return handleResponse(response);
  },

  // ─── Addresses (CRUD کامل) ─────────────────────────────────────────────────
  getAddresses: async () => {
    const response = await fetch(`${API_BASE_URL}/addresses/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  createAddress: async (addressData) => {
    const response = await fetch(`${API_BASE_URL}/addresses/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(addressData),
    });
    return handleResponse(response);
  },

  updateAddress: async (id, addressData) => {
    const response = await fetch(`${API_BASE_URL}/addresses/${id}/`, {
      credentials: 'include',
      method: 'PATCH',
      headers: createHeaders(),
      body: JSON.stringify(addressData),
    });
    return handleResponse(response);
  },

  deleteAddress: async (id) => {
    const response = await fetch(`${API_BASE_URL}/addresses/${id}/`, {
      credentials: 'include',
      method: 'DELETE',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  // ─── Live Gold Price ────────────────────────────────────────────────────────
    getLiveGoldPrice: async () => {
    const response = await fetch(`${API_BASE_URL}/gold-price/live/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

  sendSupportMessage: async (message) => {
    const response = await fetch(`${API_BASE_URL}/support/send/`, {
      credentials: 'include',
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ message }),
    });
    return handleResponse(response);
  },

  getSupportMessages: async () => {
    const response = await fetch(`${API_BASE_URL}/support/messages/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },

    // تعداد پاسخ‌های خوانده‌نشده‌ی ادمین (برای نشان قرمز روی دکمه‌ی چت)
  getSupportUnread: async () => {
    const response = await fetch(`${API_BASE_URL}/support/unread/`, {
      credentials: 'include',
      headers: createHeaders(),
    });
    return handleResponse(response);
  },
};

